import * as Sentry from '@sentry/browser-bridge';
import type { IntegrationFn } from '@sentry/types';

// wrap() creates a deferred optional-chaining wrapper for a Sentry method.
// Deferred evaluation is intentional: `Sentry` (= window.Sentry on the client) may be
// undefined at module evaluation time. The ?. check fires only when the function is called,
// so the module loads safely even when the CDN script hasn't loaded or was blocked.
function wrap<K extends keyof typeof Sentry>(key: K): (typeof Sentry)[K] {
  return ((...args: unknown[]) => (Sentry as any)?.[key]?.(...args)) as (typeof Sentry)[K];
}

// Some Sentry exports infer to function types whose parameter/return types come from
// non-exported sub-paths (e.g. `CaptureConsoleOptions` lives in
// `@sentry/integrations/types/captureconsole` but isn't re-exported). With
// `declaration: true` in the root tsconfig, TS4023 fires because those types
// "cannot be named" in declaration output. Annotating with public types from
// `@sentry/types` avoids the unnameable references while keeping correct callability.
type SentryFnProxy = (...args: unknown[]) => unknown;

// All functions present in both @sentry/react and @sentry/node, excluding ES6 classes
// (Hub, Scope) and React/router-specific exports. See SentryProxyMethods.test.ts for the
// automated completeness check.
export const addBreadcrumb = wrap('addBreadcrumb');
export const addEventProcessor = wrap('addEventProcessor');
export const addGlobalEventProcessor = wrap('addGlobalEventProcessor');
export const addIntegration = wrap('addIntegration');
export const captureConsoleIntegration: IntegrationFn = wrap('captureConsoleIntegration');
export const captureEvent = wrap('captureEvent');
export const captureException = wrap('captureException');
export const captureMessage = wrap('captureMessage');
export const captureSession = wrap('captureSession');
export const close = wrap('close');
export const configureScope = wrap('configureScope');
export const contextLinesIntegration: IntegrationFn = wrap('contextLinesIntegration');
export const continueTrace = wrap('continueTrace') as unknown as SentryFnProxy;
export const createTransport = wrap('createTransport');
export const debugIntegration: IntegrationFn = wrap('debugIntegration');
export const dedupeIntegration = wrap('dedupeIntegration');
export const defaultStackParser = wrap('defaultStackParser');
export const endSession = wrap('endSession');
export const extraErrorDataIntegration: IntegrationFn = wrap('extraErrorDataIntegration');
export const extractTraceparentData = wrap('extractTraceparentData');
export const flush = wrap('flush');
export const functionToStringIntegration = wrap('functionToStringIntegration');
export const getActiveSpan = wrap('getActiveSpan');
export const getActiveTransaction = wrap('getActiveTransaction');
export const getClient = wrap('getClient');
export const getCurrentHub = wrap('getCurrentHub');
export const getCurrentScope = wrap('getCurrentScope');
export const getDefaultIntegrations = wrap('getDefaultIntegrations');
export const getHubFromCarrier = wrap('getHubFromCarrier');
export const getSpanStatusFromHttpCode = wrap('getSpanStatusFromHttpCode');
export const httpClientIntegration: IntegrationFn = wrap('httpClientIntegration');
export const inboundFiltersIntegration = wrap('inboundFiltersIntegration');
export const init = wrap('init');
export const isInitialized = wrap('isInitialized');
export const lastEventId = wrap('lastEventId');
export const linkedErrorsIntegration: IntegrationFn = wrap('linkedErrorsIntegration');
export const makeMain = wrap('makeMain');
export const parameterize = wrap('parameterize');
export const reportingObserverIntegration: IntegrationFn = wrap('reportingObserverIntegration');
export const rewriteFramesIntegration: IntegrationFn = wrap('rewriteFramesIntegration');
export const sessionTimingIntegration = wrap('sessionTimingIntegration');
export const setContext = wrap('setContext');
export const setCurrentClient = wrap('setCurrentClient');
export const setExtra = wrap('setExtra');
export const setExtras = wrap('setExtras');
export const setHttpStatus = wrap('setHttpStatus');
export const setMeasurement = wrap('setMeasurement');
export const setTag = wrap('setTag');
export const setTags = wrap('setTags');
export const setUser = wrap('setUser');
export const spanStatusfromHttpCode = wrap('spanStatusfromHttpCode');
export const startInactiveSpan = wrap('startInactiveSpan');
export const startSession = wrap('startSession');
export const startSpan = wrap('startSpan');
export const startSpanManual = wrap('startSpanManual');
export const startTransaction = wrap('startTransaction');
export const trace = wrap('trace');
export const withActiveSpan = wrap('withActiveSpan');
export const withIsolationScope = wrap('withIsolationScope');
export const withScope = wrap('withScope');
